import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  CheckCircle2,
  Factory,
  Calculator,
  ShoppingBag,
  Briefcase,
  Cog,
  BarChart3,
  Eye,
  Database,
  FileText,
  Shield,
  TrendingUp,
  Users,
  Package,
  Search,
  Heart,
  MessageSquare,
  FileCheck,
  BookOpen,
  Sparkles,
  AlertTriangle,
  Clock,
  DollarSign,
  Zap,
  Target,
  Award
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  FadeInUp,
  StaggerContainer,
  StaggerItem,
  AnimatedBlobs,
  AnimatedCounter
} from '../components/AnimationComponents';

// Full industry data for sub-pages
const industryPagesData = {
  manufacturing: {
    id: 'manufacturing',
    name: 'Manufacturing',
    icon: Factory,
    heroHeadline: "AI That Runs Your Floor, Not the Other Way Around",
    heroSubheadline: "Canadian manufacturers are cutting downtime, automating production scheduling, and predicting equipment failures before they cost six figures. Here's how.",
    color: "from-blue-600 to-cyan-500",
    bgColor: "bg-blue-50",
    iconColor: "text-blue-600",
    lightColor: "bg-blue-100",
    
    painPoints: [
      {
        icon: AlertTriangle,
        title: "Unplanned Downtime",
        description: "You're still reacting to equipment failures instead of predicting them. Every hour of downtime costs thousands, and it's almost always preventable."
      },
      {
        icon: Clock,
        title: "Manual Scheduling Chaos",
        description: "Production schedules live in spreadsheets. When demand shifts or a machine goes down, your team scrambles to replan everything manually."
      },
      {
        icon: Eye,
        title: "Quality Control Blind Spots",
        description: "Defects slip through because manual inspection can't keep up with line speed. You catch problems too late, after they've already shipped."
      }
    ],
    
    services: [
      {
        icon: Cog,
        title: "Predictive Maintenance AI",
        description: "Monitor equipment health in real time. Our AI analyzes vibration, temperature, and operational data to predict failures 2-4 weeks before they happen.",
        benefits: ["Reduce unplanned downtime by up to 40%", "Extend equipment lifespan", "Optimize maintenance schedules", "Lower repair costs"]
      },
      {
        icon: Package,
        title: "Production & Inventory Automation",
        description: "Automate the entire flow, from demand sensing to production scheduling to reorder triggers. Your ERP finally works the way it should.",
        benefits: ["Real-time inventory visibility", "Automated reorder points", "Dynamic production scheduling", "Supply chain alerts"]
      },
      {
        icon: Eye,
        title: "Quality Control & Defect Detection",
        description: "Computer vision that catches what human eyes miss. Inspect 100% of output at line speed with sub-millimeter accuracy.",
        benefits: ["Catch defects before shipping", "Reduce scrap and rework", "Audit-ready quality data", "Continuous improvement insights"]
      },
      {
        icon: Database,
        title: "ERP + AI Integration",
        description: "Connect your existing ERP (SAP, Odoo, NetSuite, ERPNext) with AI dashboards. See what's actually happening, not what happened last month.",
        benefits: ["Real-time operational insights", "Custom AI dashboards", "No rip-and-replace required", "Works with your current stack"]
      },
      {
        icon: BarChart3,
        title: "Demand Forecasting",
        description: "Stop guessing what to produce. Machine learning models that factor in seasonality, market trends, and your historical data.",
        benefits: ["Accurate demand predictions", "Reduce overproduction", "Better capacity planning", "Improved cash flow"]
      },
      {
        icon: Zap,
        title: "Energy & Resource Optimization",
        description: "AI that identifies waste in your operations, from energy consumption to raw material usage, and recommends optimizations.",
        benefits: ["Reduce energy costs", "Lower carbon footprint", "Optimize resource usage", "Meet sustainability goals"]
      }
    ],
    
    stats: [
      { value: 40, suffix: "%", label: "Reduction in unplanned downtime" },
      { value: 25, suffix: "%", label: "Lower maintenance costs" },
      { value: 15, suffix: "%", label: "Increase in throughput" }
    ],
    
    caseStudy: {
      company: "Northern Fabrication Inc.",
      location: "Calgary, AB",
      challenge: "Frequent equipment failures causing $50K+ monthly losses",
      solution: "Implemented predictive maintenance AI across 12 production machines",
      results: ["62% reduction in unplanned downtime", "$420K annual savings", "3-month payback period"],
      quote: "We went from firefighting every week to planning maintenance on our terms. The ROI was obvious within 90 days."
    },
    
    faqs: [
      {
        question: "Will this work with our existing ERP system?",
        answer: "Yes. We integrate with SAP, Odoo, NetSuite, ERPNext, and most major manufacturing ERPs. Our AI layer sits on top of your existing systems, no rip-and-replace required."
      },
      {
        question: "How long does implementation take?",
        answer: "Most manufacturing AI implementations take 8-12 weeks from kickoff to go-live. We start with a pilot on one line or machine, prove results, then scale."
      },
      {
        question: "What data do we need to get started?",
        answer: "We work with whatever you have, machine logs, sensor data, maintenance records, production schedules. Our team handles the data integration and cleaning."
      }
    ],
    
    ctaText: "Book a Free Manufacturing AI Call"
  },
  
  accounting: {
    id: 'accounting',
    name: 'Accounting & Finance',
    icon: Calculator,
    heroHeadline: "Stop Doing Bookkeeping. Start Advising.",
    heroSubheadline: "Canadian accounting firms are automating the grunt work, reconciliation, categorization, compliance, so their teams can focus on advisory work that actually moves the needle.",
    color: "from-emerald-600 to-teal-500",
    bgColor: "bg-emerald-50",
    iconColor: "text-emerald-600",
    lightColor: "bg-emerald-100",
    
    painPoints: [
      {
        icon: Clock,
        title: "Drowning in Data Entry",
        description: "Your team is still manually categorizing transactions and reconciling accounts. It's tedious, error-prone, and keeps you from higher-value work."
      },
      {
        icon: Shield,
        title: "CRA Compliance Anxiety",
        description: "Tax rules change constantly. You're spending more time staying compliant than advising clients, and one mistake can cost your reputation."
      },
      {
        icon: BarChart3,
        title: "Reactive, Not Proactive",
        description: "By the time you deliver financial reports, the numbers are already old. Clients want real-time insights, but you're stuck in last quarter's data."
      }
    ],
    
    services: [
      {
        icon: FileCheck,
        title: "Automated Bookkeeping & Reconciliation",
        description: "AI that categorizes transactions, matches invoices, and reconciles accounts automatically. Flags anomalies for human review, handles the rest.",
        benefits: ["Save 15+ hours per week", "99%+ categorization accuracy", "Automatic bank reconciliation", "Real-time books"]
      },
      {
        icon: Shield,
        title: "Tax & Compliance Automation",
        description: "Stay ahead of CRA requirements without the stress. Automated GST/HST calculations, compliance checks, and audit-ready documentation.",
        benefits: ["CRA-compliant reporting", "Automated GST/HST filing prep", "Audit trail documentation", "Real-time compliance alerts"]
      },
      {
        icon: TrendingUp,
        title: "AI-Powered Financial Forecasting",
        description: "Predict cash flow, revenue trends, and risk scenarios using live data. Give clients forward-looking insights, not backward-looking reports.",
        benefits: ["Cash flow forecasting", "Revenue predictions", "Risk scenario modeling", "What-if analysis"]
      },
      {
        icon: BarChart3,
        title: "Client Reporting & Advisory Tools",
        description: "Generate branded financial reports automatically. Surface the insights your clients care about, without hours of manual analysis.",
        benefits: ["Automated report generation", "Custom branded templates", "Key insight highlighting", "Client portal access"]
      },
      {
        icon: MessageSquare,
        title: "AI Client Communication",
        description: "Draft client emails, summarize complex financials, and answer routine questions with AI assistance that sounds like your team.",
        benefits: ["Faster client responses", "Consistent communication", "Document summarization", "Meeting prep automation"]
      },
      {
        icon: Database,
        title: "Multi-Entity Consolidation",
        description: "Managing multiple companies or entities? AI that consolidates financials, eliminates intercompany transactions, and produces group reports.",
        benefits: ["Automated consolidation", "Intercompany elimination", "Multi-currency support", "Group reporting"]
      }
    ],
    
    stats: [
      { value: 15, suffix: "+", label: "Hours saved per week" },
      { value: 99, suffix: "%", label: "Categorization accuracy" },
      { value: 60, suffix: "%", label: "Faster month-end close" }
    ],
    
    caseStudy: {
      company: "West Coast Accounting Partners",
      location: "Vancouver, BC",
      challenge: "Partners spending 60% of time on bookkeeping instead of advisory",
      solution: "Implemented automated bookkeeping and client reporting AI",
      results: ["70% reduction in manual data entry", "2x increase in advisory revenue", "Client satisfaction up 35%"],
      quote: "We went from being bookkeepers to being advisors. Our clients notice the difference, and they're willing to pay for it."
    },
    
    faqs: [
      {
        question: "Does this work with QuickBooks/Xero/Sage?",
        answer: "Yes. We integrate with all major accounting platforms including QuickBooks Online, Xero, Sage, and FreshBooks. Your team keeps using the tools they know."
      },
      {
        question: "How accurate is the transaction categorization?",
        answer: "Our AI achieves 99%+ accuracy after learning from your firm's categorization patterns. It improves over time and flags low-confidence items for human review."
      },
      {
        question: "Is client data secure?",
        answer: "Absolutely. All data is encrypted, stored on Canadian servers, and we're fully compliant with PIPEDA. We never share or use client data to train public models."
      }
    ],
    
    ctaText: "Book a Free Accounting AI Call"
  },
  
  retail: {
    id: 'retail',
    name: 'Retail & Ecommerce',
    icon: ShoppingBag,
    heroHeadline: "Compete with the Giants, Without Their Budget",
    heroSubheadline: "Canadian retailers are using AI to personalize experiences, predict demand, and keep customers coming back. The big guys have been doing this for years. Now it's your turn.",
    color: "from-purple-600 to-pink-500",
    bgColor: "bg-purple-50",
    iconColor: "text-purple-600",
    lightColor: "bg-purple-100",
    
    painPoints: [
      {
        icon: Package,
        title: "Inventory Nightmares",
        description: "You're either sitting on dead stock or running out of bestsellers. Demand forecasting feels like guessing, and every mistake hits your margins."
      },
      {
        icon: Users,
        title: "Generic Customer Experience",
        description: "Amazon knows what customers want before they do. Your site shows everyone the same thing, and conversion rates show it."
      },
      {
        icon: TrendingUp,
        title: "Marketing Money Pits",
        description: "You're spending on ads but can't tell what's actually driving sales. Customer acquisition costs keep climbing while lifetime value stays flat."
      }
    ],
    
    services: [
      {
        icon: Sparkles,
        title: "AI-Powered Product Recommendations",
        description: "Show every customer products they'll actually buy. Personalized recommendations across web, email, and in-store that increase AOV by 20-30%.",
        benefits: ["Personalized product suggestions", "Cross-sell and upsell automation", "Email recommendation blocks", "In-store kiosk integration"]
      },
      {
        icon: Package,
        title: "Demand Forecasting & Inventory AI",
        description: "Know what's going to sell before it does. Machine learning that factors in trends, seasonality, and your specific sales patterns.",
        benefits: ["Accurate demand predictions", "Automated reorder points", "Reduce overstock by 30%", "Never miss a bestseller"]
      },
      {
        icon: Search,
        title: "AI SEO & Ecommerce Content",
        description: "Product descriptions that rank and convert. Category pages optimized for search. All generated at scale without sounding robotic.",
        benefits: ["SEO-optimized product copy", "Category page content", "Bilingual content (EN/FR)", "Bulk description generation"]
      },
      {
        icon: Heart,
        title: "Customer Retention & Churn Prediction",
        description: "Identify at-risk customers before they leave. Trigger personalized win-back campaigns automatically based on behavior signals.",
        benefits: ["Churn prediction modeling", "Automated win-back campaigns", "Loyalty program optimization", "Customer lifetime value tracking"]
      },
      {
        icon: Target,
        title: "Smart Pricing & Promotions",
        description: "Dynamic pricing that maximizes margin without losing customers. Know when to discount, what to bundle, and how to compete.",
        benefits: ["Competitive price monitoring", "Dynamic pricing rules", "Promotion effectiveness analysis", "Margin optimization"]
      },
      {
        icon: BarChart3,
        title: "Unified Retail Analytics",
        description: "See online and offline sales in one dashboard. Understand the full customer journey from first click to repeat purchase.",
        benefits: ["Omnichannel analytics", "Customer journey mapping", "Attribution modeling", "Real-time sales dashboards"]
      }
    ],
    
    stats: [
      { value: 30, suffix: "%", label: "Higher average order value" },
      { value: 25, suffix: "%", label: "Reduction in overstock" },
      { value: 40, suffix: "%", label: "Improvement in retention" }
    ],
    
    caseStudy: {
      company: "Vancouver Outdoor Gear",
      location: "Vancouver, BC",
      challenge: "Low conversion rates and high inventory carrying costs on Shopify store",
      solution: "Implemented AI recommendations and demand forecasting",
      results: ["85% increase in online revenue", "28% higher AOV", "35% reduction in dead stock"],
      quote: "We went from competing on price to competing on experience. Our customers feel like we actually know them now."
    },
    
    faqs: [
      {
        question: "Does this work with Shopify/WooCommerce/Magento?",
        answer: "Yes. We have native integrations with Shopify, WooCommerce, Magento, and BigCommerce. Implementation typically takes 2-3 weeks for basic features."
      },
      {
        question: "How much traffic do we need for AI to work?",
        answer: "AI recommendations start showing value at around 1,000 monthly visitors. For demand forecasting, we need about 6 months of sales history to build accurate models."
      },
      {
        question: "Can this work for physical retail too?",
        answer: "Absolutely. Our inventory and demand forecasting works across channels. We also offer in-store kiosk solutions for personalized recommendations."
      }
    ],
    
    ctaText: "Book a Free Retail AI Call"
  },
  
  b2b: {
    id: 'b2b',
    name: 'B2B Services',
    icon: Briefcase,
    heroHeadline: "Close More Deals Without Hiring More Reps",
    heroSubheadline: "B2B services companies are using AI to fill pipelines, automate proposals, and scale delivery, without the overhead. Your competitors are already doing it.",
    color: "from-orange-600 to-amber-500",
    bgColor: "bg-orange-50",
    iconColor: "text-orange-600",
    lightColor: "bg-orange-100",
    
    painPoints: [
      {
        icon: Users,
        title: "Feast or Famine Pipeline",
        description: "When you're busy delivering, you stop selling. When you stop selling, the pipeline dries up. It's a cycle you can't seem to break."
      },
      {
        icon: FileText,
        title: "Proposal Time Sink",
        description: "Every proposal takes hours to customize. Half of them don't close anyway. Your team is spending more time writing than selling."
      },
      {
        icon: Clock,
        title: "Knowledge Trapped in Heads",
        description: "Your best people spend half their time answering the same questions. Onboarding takes months. Knowledge walks out the door when people leave."
      }
    ],
    
    services: [
      {
        icon: Users,
        title: "AI Lead Scoring & CRM Automation",
        description: "Automatically score inbound leads based on fit and intent. Route to the right rep. Trigger nurture sequences. Never let a hot lead go cold.",
        benefits: ["Predictive lead scoring", "Automated lead routing", "CRM data enrichment", "Follow-up sequence automation"]
      },
      {
        icon: FileText,
        title: "Proposal & Document Automation",
        description: "Generate tailored proposals, SOWs, and contracts in minutes. Pull from your best templates. Customize for each client automatically.",
        benefits: ["60% faster proposal creation", "Consistent quality", "Dynamic pricing insertion", "E-signature integration"]
      },
      {
        icon: BookOpen,
        title: "LLM-Powered Knowledge Bases",
        description: "Turn your documentation into an AI assistant. Answer client questions instantly. Onboard new hires faster. Stop answering the same questions.",
        benefits: ["Instant answers from your docs", "Client self-service portal", "New hire onboarding bot", "Continuous learning from usage"]
      },
      {
        icon: MessageSquare,
        title: "AI-Driven Content & Thought Leadership",
        description: "Scale your LinkedIn presence, case studies, and blog content. AI that sounds like your team wrote it, because it learned from them.",
        benefits: ["LinkedIn content at scale", "Case study generation", "SEO blog content", "Email newsletter drafts"]
      },
      {
        icon: BarChart3,
        title: "Sales Intelligence & Forecasting",
        description: "Know which deals will close and which are at risk. AI that analyzes email sentiment, meeting patterns, and deal velocity.",
        benefits: ["Deal risk scoring", "Revenue forecasting", "Activity-based insights", "Pipeline health monitoring"]
      },
      {
        icon: Zap,
        title: "Client Success Automation",
        description: "Proactive client health monitoring. Automated check-ins. Early warning on churn risk. Turn clients into advocates without manual tracking.",
        benefits: ["Client health scoring", "Automated check-ins", "Churn risk alerts", "Expansion opportunity flagging"]
      }
    ],
    
    stats: [
      { value: 3, suffix: "x", label: "More qualified meetings" },
      { value: 60, suffix: "%", label: "Faster proposal turnaround" },
      { value: 45, suffix: "%", label: "Reduction in sales cycle" }
    ],
    
    caseStudy: {
      company: "TechFlow Solutions",
      location: "Toronto, ON",
      challenge: "Sales team spending 40% of time on admin instead of selling",
      solution: "Implemented AI lead scoring, proposal automation, and CRM workflows",
      results: ["2.8x increase in qualified meetings", "Proposal time cut from 4 hours to 45 minutes", "Sales cycle shortened by 5 weeks"],
      quote: "Our sales team finally gets to sell. The AI handles the busywork. Our close rate is up 35% because reps are focused on the right deals."
    },
    
    faqs: [
      {
        question: "Which CRMs do you integrate with?",
        answer: "We integrate with Salesforce, HubSpot, Pipedrive, and most major CRMs. Our AI layer works on top of your existing setup, no platform change required."
      },
      {
        question: "How does the knowledge base AI learn?",
        answer: "It ingests your existing documentation, SOPs, and past communications. Then it learns from usage, every question asked and answered makes it smarter."
      },
      {
        question: "Will the content sound like us?",
        answer: "Yes. We train the AI on your existing content, tone, and style. It learns how your team writes, not how generic AI writes. You review and refine until it's right."
      }
    ],
    
    ctaText: "Book a Free B2B AI Call"
  }
};

// Industry Page Component
export const IndustryPage = () => {
  const { industryId } = useParams();
  const industry = industryPagesData[industryId];

  if (!industry) {
    return (
      <div className="section-padding text-center">
        <h1 className="font-outfit text-3xl font-bold text-[#0A2540] mb-4">Industry Not Found</h1>
        <Link to="/ai-solutions">
          <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full">
            View All AI Solutions
          </Button>
        </Link>
      </div>
    );
  }

  const IconComponent = industry.icon;

  return (
    <div data-testid={`industry-page-${industryId}`}>
      {/* Hero Section */}
      <section className="section-padding bg-[#0A2540] relative overflow-hidden">
        <AnimatedBlobs />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <FadeInUp>
              <div className={`inline-flex items-center gap-2 ${industry.lightColor} px-4 py-2 rounded-full mb-6`}>
                <IconComponent className={`w-5 h-5 ${industry.iconColor}`} />
                <span className={`text-sm font-medium ${industry.iconColor}`}>AI for {industry.name}</span>
              </div>
            </FadeInUp>
            
            <FadeInUp delay={0.1}>
              <h1 className="font-outfit text-4xl md:text-5xl font-bold text-white mb-6">
                {industry.heroHeadline}
              </h1>
            </FadeInUp>
            
            <FadeInUp delay={0.2}>
              <p className="text-white/70 text-lg mb-8">
                {industry.heroSubheadline}
              </p>
            </FadeInUp>
            
            <FadeInUp delay={0.3}>
              <Link to={`/contact?industry=${industryId}`}>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg">
                    {industry.ctaText}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>
              </Link>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="container-custom">
          <StaggerContainer className="grid grid-cols-3 gap-8" staggerDelay={0.1}>
            {industry.stats.map((stat, index) => (
              <StaggerItem key={index}>
                <div className="text-center">
                  <div className={`text-3xl md:text-4xl font-bold bg-gradient-to-r ${industry.color} bg-clip-text text-transparent`}>
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} duration={2} />
                  </div>
                  <div className="text-sm text-slate-600 mt-1">{stat.label}</div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <FadeInUp className="text-center mb-12">
            <h2 className="font-outfit text-3xl font-bold text-[#0A2540]">
              The Challenges {industry.name} Businesses Face
            </h2>
          </FadeInUp>
          
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8" staggerDelay={0.1}>
            {industry.painPoints.map((pain, index) => (
              <StaggerItem key={index}>
                <motion.div 
                  className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm h-full"
                  whileHover={{ y: -5, boxShadow: "0 20px 40px -10px rgba(0,0,0,0.1)" }}
                >
                  <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mb-6">
                    <pain.icon className="w-7 h-7 text-red-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-[#0A2540] mb-3">{pain.title}</h3>
                  <p className="text-slate-600">{pain.description}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding">
        <div className="container-custom">
          <FadeInUp className="text-center mb-12">
            <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Our AI Services</span>
            <h2 className="font-outfit text-3xl font-bold text-[#0A2540] mt-4">
              AI Solutions for {industry.name}
            </h2>
          </FadeInUp>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {industry.services.map((service, index) => (
              <FadeInUp key={index} delay={index * 0.1}>
                <motion.div 
                  className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm h-full group hover:shadow-xl hover:border-slate-200 transition-all"
                  whileHover={{ y: -5 }}
                >
                  <div className="flex items-start gap-6">
                    <motion.div 
                      className={`w-16 h-16 bg-gradient-to-br ${industry.color} rounded-2xl flex items-center justify-center flex-shrink-0`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <service.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <div>
                      <h3 className="font-outfit text-xl font-semibold text-[#0A2540] mb-3 group-hover:text-[#2563EB] transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-slate-600 mb-4">{service.description}</p>
                      <ul className="grid grid-cols-2 gap-2">
                        {service.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-slate-500">
                            <CheckCircle2 className="w-4 h-4 text-[#2563EB] flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className={`section-padding bg-gradient-to-br ${industry.color}`}>
        <div className="container-custom">
          <FadeInUp className="text-center mb-12">
            <h2 className="font-outfit text-3xl font-bold text-white">
              How It Works
            </h2>
          </FadeInUp>
          
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto" staggerDelay={0.15}>
            {[
              { step: "01", title: "Assess", description: "We analyze your current operations, identify AI opportunities, and build a custom roadmap." },
              { step: "02", title: "Build", description: "Our team implements AI solutions tailored to your specific workflows and systems." },
              { step: "03", title: "Launch", description: "Go live with training and support. See results within weeks, not months." }
            ].map((step, index) => (
              <StaggerItem key={index}>
                <motion.div 
                  className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 rounded-2xl text-center"
                  whileHover={{ y: -5 }}
                >
                  <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-xl font-bold text-[#0A2540]">{step.step}</span>
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-white/70">{step.description}</p>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Case Study */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <FadeInUp>
              <div className="bg-white p-8 md:p-12 rounded-3xl border border-slate-100 shadow-lg">
                <div className="flex items-center gap-2 mb-6">
                  <Award className={`w-6 h-6 ${industry.iconColor}`} />
                  <span className={`text-sm font-semibold ${industry.iconColor} uppercase tracking-wider`}>Case Study</span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-outfit text-2xl font-bold text-[#0A2540] mb-2">
                      {industry.caseStudy.company}
                    </h3>
                    <p className="text-slate-500 mb-4">{industry.caseStudy.location}</p>
                    
                    <div className="mb-6">
                      <h4 className="font-semibold text-[#0A2540] mb-2">The Challenge</h4>
                      <p className="text-slate-600">{industry.caseStudy.challenge}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-[#0A2540] mb-2">Our Solution</h4>
                      <p className="text-slate-600">{industry.caseStudy.solution}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-[#0A2540] mb-4">Results</h4>
                    <ul className="space-y-3 mb-6">
                      {industry.caseStudy.results.map((result, i) => (
                        <li key={i} className="flex items-center gap-3">
                          <div className={`w-8 h-8 ${industry.bgColor} rounded-lg flex items-center justify-center`}>
                            <CheckCircle2 className={`w-5 h-5 ${industry.iconColor}`} />
                          </div>
                          <span className="font-medium text-[#0A2540]">{result}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <blockquote className={`border-l-4 ${industry.iconColor.replace('text-', 'border-')} pl-4 italic text-slate-600`}>
                      "{industry.caseStudy.quote}"
                    </blockquote>
                  </div>
                </div>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <div className="container-custom">
          <FadeInUp className="text-center mb-12">
            <h2 className="font-outfit text-3xl font-bold text-[#0A2540]">
              Frequently Asked Questions
            </h2>
          </FadeInUp>
          
          <FadeInUp delay={0.2} className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {industry.faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white border border-slate-100 rounded-2xl px-6"
                >
                  <AccordionTrigger className="text-left font-medium text-[#0A2540] hover:no-underline py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeInUp>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="section-padding bg-[#0A2540] relative overflow-hidden">
        <AnimatedBlobs />
        <div className="container-custom text-center relative z-10">
          <FadeInUp>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Bring AI into Your {industry.name} Business?
            </h2>
          </FadeInUp>
          
          <FadeInUp delay={0.2}>
            <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
              Let's build it together. Start with a free consultation to see what's possible.
            </p>
          </FadeInUp>
          
          <FadeInUp delay={0.4}>
            <Link to={`/contact?industry=${industryId}`}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                <Button className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-10 py-6 text-lg font-medium shadow-lg shadow-[#2563EB]/25">
                  {industry.ctaText}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </Link>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default IndustryPage;
